import React, { useState, useEffect, useCallback } from 'react';
import { STAGE_LABELS, STAGE_COLORS, PRIORITY_COLORS } from './CommercialDashboard';
import { useIsMobile } from '../../hooks/useIsMobile';

const API = process.env.REACT_APP_BACKEND_URL;
const h = () => ({ 'Content-Type': 'application/json' });
const authOpts = (opts = {}) => ({ ...opts, credentials: 'include', headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) } });

const SECTION_CONFIG = {
  new_targets: { title: 'New Targets — Make First Contact', color: '#3b82f6', icon: 'M12 4v16m8-8H4' },
  follow_ups_due: { title: 'Follow-Ups Due', color: '#f59e0b', icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' },
  no_decision_maker: { title: 'No Decision Maker Identified', color: '#ef4444', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
  capability_followups: { title: 'Capability Package Follow-Ups', color: '#8b5cf6', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  vendor_pending: { title: 'Vendor Process Pending', color: '#ec4899', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
  bid_list_contact: { title: 'Bid List — Time to Follow Up', color: '#f97316', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
  dormant_accounts: { title: 'Dormant — Reactivate', color: '#78716c', icon: 'M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z' },
  high_value_attention: { title: 'A-Priority Accounts Need Attention', color: '#dc2626', icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' },
};

const TYPE_LABELS = {
  general_contractor: 'GC', custom_home_builder: 'Builder', commercial_builder: 'Comm Builder',
  developer: 'Developer', property_investor: 'Investor', site_contractor: 'Site', civil_contractor: 'Civil',
  ranch_developer: 'Ranch', equestrian_business: 'Equestrian', property_manager: 'Prop Mgmt',
  equipment_yard: 'Equip Yard', industrial: 'Industrial', utility_contractor: 'Utility',
  government: 'Govt', other: 'Other',
};

export default function CommercialProspecting({ navigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quickAction, setQuickAction] = useState(null);
  const [actionForm, setActionForm] = useState({ type: 'call', description: '', outcome: '', contact_method: 'call' });
  const [saving, setSaving] = useState(false);
  const [taskForm, setTaskForm] = useState({ description: '', due_days: 1, priority: 'medium' });
  const [showTaskForm, setShowTaskForm] = useState(null);
  const isMobile = useIsMobile();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sales/commercial/prospecting`, authOpts());
      if (res.ok) setData(await res.json());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const logOutcome = async (accountId) => {
    if (!actionForm.outcome) return;
    setSaving(true);
    try {
      await fetch(`${API}/api/sales/commercial/accounts/${accountId}/contact-outcome`, authOpts({
        method: 'POST',
        body: JSON.stringify({
          outcome: actionForm.outcome,
          notes: actionForm.description,
          contact_method: actionForm.contact_method,
          contact_id: null,
        }),
      }));
      setQuickAction(null);
      setActionForm({ type: 'call', description: '', outcome: '', contact_method: 'call' });
      load();
    } finally { setSaving(false); }
  };

  const logActivity = async (accountId) => {
    if (!actionForm.description.trim()) return;
    setSaving(true);
    try {
      await fetch(`${API}/api/sales/commercial/accounts/${accountId}/activities`, authOpts({
        method: 'POST',
        body: JSON.stringify({ activity_type: actionForm.type, description: actionForm.description }),
      }));
      setQuickAction(null);
      setActionForm({ type: 'call', description: '', outcome: '', contact_method: 'call' });
      load();
    } finally { setSaving(false); }
  };

  const createTask = async (accountId) => {
    if (!taskForm.description.trim()) return;
    setSaving(true);
    try {
      await fetch(`${API}/api/sales/commercial/tasks`, authOpts({
        method: 'POST',
        body: JSON.stringify({ account_id: accountId, ...taskForm }),
      }));
      setShowTaskForm(null);
      setTaskForm({ description: '', due_days: 1, priority: 'medium' });
      load();
    } finally { setSaving(false); }
  };

  const completeTask = async (taskId) => {
    await fetch(`${API}/api/sales/commercial/tasks/${taskId}/complete`, authOpts({
      method: 'POST', body: JSON.stringify({ result: 'Completed from prospecting' }),
    }));
    load();
  };

  if (loading || !data) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading prospecting workspace...</div>;

  const sections = data.sections || {};
  const summary = data.summary || {};
  const tasksDue = data.tasks_due || [];

  return (
    <div data-testid="commercial-prospecting" style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '12px 10px' : '24px 28px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: 20, flexDirection: isMobile ? 'column' : 'row', gap: 8 }}>
        <div>
          <h1 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, color: '#1e293b', margin: 0 }}>Prospecting Workspace</h1>
          <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0' }}>
            {data.date} | {summary.total_actions} action{summary.total_actions !== 1 ? 's' : ''} | {summary.tasks_due_count} task{summary.tasks_due_count !== 1 ? 's' : ''} due
          </p>
        </div>
        <button data-testid="refresh-prospecting" onClick={load}
          style={{ padding: '6px 14px', fontSize: 11, fontWeight: 600, border: '1px solid #d1d5db', borderRadius: 4, background: '#fff', color: '#475569', cursor: 'pointer', minHeight: 36 }}>
          Refresh
        </button>
      </div>

      {/* Tasks Due Today/Overdue */}
      {tasksDue.length > 0 && (
        <div data-testid="tasks-due-section" style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '14px 18px', marginBottom: 16 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: '#dc2626', margin: '0 0 8px' }}>
            Tasks Due / Overdue ({tasksDue.length})
          </h3>
          {tasksDue.map(t => (
            <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #fecaca40' }}>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 12, color: '#1e293b', fontWeight: 600 }}>{t.description}</span>
                <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 8 }}>Due: {t.due_at?.slice(0, 10)}</span>
              </div>
              <button data-testid={`complete-task-${t.id}`} onClick={() => completeTask(t.id)}
                style={{ padding: '3px 10px', fontSize: 10, fontWeight: 600, background: '#16a34a', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer' }}>
                Done
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Prospecting Sections */}
      {Object.entries(SECTION_CONFIG).map(([key, cfg]) => {
        const items = sections[key] || [];
        if (items.length === 0) return null;
        return (
          <div key={key} data-testid={`section-${key}`} style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '14px 18px', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <div style={{ width: 24, height: 24, borderRadius: 5, background: `${cfg.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={cfg.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={cfg.icon}/></svg>
              </div>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', margin: 0 }}>{cfg.title}</h3>
              <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 10, background: `${cfg.color}15`, color: cfg.color, fontWeight: 700 }}>{items.length}</span>
            </div>
            {items.map(item => (
              <div key={item.account_id} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 6 : 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                    <span style={{ width: 22, height: 22, borderRadius: '50%', background: PRIORITY_COLORS[item.priority] || '#94a3b8', color: '#fff', fontSize: 10, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{item.priority}</span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <span data-testid={`account-link-${item.account_id}`} onClick={() => navigate(`/sales/commercial/account/${item.account_id}`)}
                        style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{item.company_name}</span>
                      <div style={{ display: 'flex', gap: 8, fontSize: 10, color: '#94a3b8', marginTop: 1, flexWrap: 'wrap' }}>
                        <span>{TYPE_LABELS[item.account_type] || item.account_type}</span>
                        <span style={{ color: STAGE_COLORS[item.stage], fontWeight: 600 }}>{STAGE_LABELS[item.stage] || item.stage}</span>
                        {item.days_since_contact !== null && <span>{item.days_since_contact}d since contact</span>}
                        {item.days_since_contact === null && <span style={{ color: '#dc2626' }}>Never contacted</span>}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <button data-testid={`action-call-${item.account_id}`}
                      onClick={() => { setQuickAction({ id: item.account_id, name: item.company_name, mode: 'outcome' }); setActionForm(f => ({ ...f, contact_method: 'call' })); }}
                      style={qaBtnStyle('#3b82f6')}>Call</button>
                    <button data-testid={`action-email-${item.account_id}`}
                      onClick={() => { setQuickAction({ id: item.account_id, name: item.company_name, mode: 'outcome' }); setActionForm(f => ({ ...f, contact_method: 'email' })); }}
                      style={qaBtnStyle('#8b5cf6')}>Email</button>
                    <button data-testid={`action-note-${item.account_id}`}
                      onClick={() => { setQuickAction({ id: item.account_id, name: item.company_name, mode: 'note' }); setActionForm(f => ({ ...f, type: 'note' })); }}
                      style={qaBtnStyle('#64748b')}>Note</button>
                    <button data-testid={`action-task-${item.account_id}`}
                      onClick={() => setShowTaskForm(item.account_id)}
                      style={qaBtnStyle('#f59e0b')}>Task</button>
                    <button data-testid={`action-stage-${item.account_id}`}
                      onClick={() => navigate(`/sales/commercial/account/${item.account_id}`)}
                      style={qaBtnStyle('#16a34a')}>Open</button>
                  </div>
                </div>

                {/* Quick Action Inline Panel */}
                {quickAction?.id === item.account_id && quickAction.mode === 'outcome' && (
                  <div style={{ marginTop: 8, padding: '10px 12px', background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                      Log {actionForm.contact_method} outcome for {quickAction.name}
                    </div>
                    <select data-testid="outcome-select" value={actionForm.outcome} onChange={e => setActionForm(f => ({ ...f, outcome: e.target.value }))}
                      style={{ width: '100%', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, marginBottom: 6 }}>
                      <option value="">Select outcome...</option>
                      {OUTCOME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <textarea data-testid="outcome-notes" value={actionForm.description} onChange={e => setActionForm(f => ({ ...f, description: e.target.value }))}
                      placeholder="Notes (optional)" rows={2} style={{ width: '100%', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, resize: 'vertical', boxSizing: 'border-box', marginBottom: 6 }} />
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button onClick={() => setQuickAction(null)} style={{ padding: '4px 10px', fontSize: 10, border: '1px solid #d1d5db', borderRadius: 3, background: '#fff', cursor: 'pointer', color: '#64748b' }}>Cancel</button>
                      <button data-testid="submit-outcome" onClick={() => logOutcome(item.account_id)} disabled={!actionForm.outcome || saving}
                        style={{ padding: '4px 12px', fontSize: 10, fontWeight: 600, background: actionForm.outcome ? '#c0392b' : '#94a3b8', color: '#fff', border: 'none', borderRadius: 3, cursor: actionForm.outcome ? 'pointer' : 'default' }}>
                        {saving ? 'Logging...' : 'Log Outcome'}
                      </button>
                    </div>
                  </div>
                )}
                {quickAction?.id === item.account_id && quickAction.mode === 'note' && (
                  <div style={{ marginTop: 8, padding: '10px 12px', background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                    <textarea data-testid="note-input" value={actionForm.description} onChange={e => setActionForm(f => ({ ...f, description: e.target.value }))}
                      placeholder="Add a note..." rows={2} style={{ width: '100%', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, resize: 'vertical', boxSizing: 'border-box', marginBottom: 6 }} />
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button onClick={() => setQuickAction(null)} style={{ padding: '4px 10px', fontSize: 10, border: '1px solid #d1d5db', borderRadius: 3, background: '#fff', cursor: 'pointer', color: '#64748b' }}>Cancel</button>
                      <button data-testid="submit-note" onClick={() => logActivity(item.account_id)} disabled={!actionForm.description.trim() || saving}
                        style={{ padding: '4px 12px', fontSize: 10, fontWeight: 600, background: '#c0392b', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer' }}>
                        {saving ? 'Saving...' : 'Save Note'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Create Task Inline */}
                {showTaskForm === item.account_id && (
                  <div style={{ marginTop: 8, padding: '10px 12px', background: '#fffbeb', borderRadius: 6, border: '1px solid #fde68a' }}>
                    <input data-testid="task-desc-input" value={taskForm.description} onChange={e => setTaskForm(f => ({ ...f, description: e.target.value }))}
                      placeholder="Task description..." style={{ width: '100%', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, boxSizing: 'border-box', marginBottom: 6 }} />
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <select value={taskForm.due_days} onChange={e => setTaskForm(f => ({ ...f, due_days: parseInt(e.target.value) }))}
                        style={{ padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 3, fontSize: 10 }}>
                        <option value={1}>Due in 1 day</option><option value={2}>2 days</option><option value={3}>3 days</option>
                        <option value={5}>5 days</option><option value={7}>1 week</option><option value={14}>2 weeks</option>
                      </select>
                      <select value={taskForm.priority} onChange={e => setTaskForm(f => ({ ...f, priority: e.target.value }))}
                        style={{ padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 3, fontSize: 10 }}>
                        <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                      </select>
                      <div style={{ flex: 1 }} />
                      <button onClick={() => setShowTaskForm(null)} style={{ padding: '4px 10px', fontSize: 10, border: '1px solid #d1d5db', borderRadius: 3, background: '#fff', cursor: 'pointer', color: '#64748b' }}>Cancel</button>
                      <button data-testid="submit-task" onClick={() => createTask(item.account_id)} disabled={!taskForm.description.trim() || saving}
                        style={{ padding: '4px 12px', fontSize: 10, fontWeight: 600, background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer' }}>
                        {saving ? 'Creating...' : 'Create Task'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })}

      {summary.total_actions === 0 && tasksDue.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" style={{ marginBottom: 12 }}><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#64748b' }}>All caught up!</div>
          <div style={{ fontSize: 12 }}>No prospecting actions needed right now.</div>
        </div>
      )}
    </div>
  );
}

const OUTCOME_OPTIONS = [
  { value: 'no_answer', label: 'No Answer' },
  { value: 'voicemail', label: 'Voicemail' },
  { value: 'wrong_contact', label: 'Wrong Contact' },
  { value: 'decision_maker_identified', label: 'Decision Maker Identified' },
  { value: 'spoke_with_decision_maker', label: 'Spoke With Decision Maker' },
  { value: 'send_capabilities', label: 'Send Capabilities' },
  { value: 'send_vendor_documents', label: 'Send Vendor Documents' },
  { value: 'already_has_vendor', label: 'Already Has Vendor' },
  { value: 'open_to_new_vendors', label: 'Open to New Vendors' },
  { value: 'added_to_bid_list', label: 'Added to Bid List' },
  { value: 'vendor_application_required', label: 'Vendor Application Required' },
  { value: 'bid_opportunity_available', label: 'Bid Opportunity Available' },
  { value: 'follow_up_later', label: 'Follow Up Later' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'do_not_contact', label: 'Do Not Contact' },
];

function qaBtnStyle(color) {
  return { padding: '5px 10px', fontSize: 10, fontWeight: 600, background: `${color}10`, color, border: `1px solid ${color}30`, borderRadius: 4, cursor: 'pointer', minHeight: 32 };
}

export { OUTCOME_OPTIONS };
